import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/mocks/movies";

interface HeroCarouselProps {
  works: Work[];
  loading?: boolean;
}

export default function HeroCarousel({ works, loading = false }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = works.length;

  const goTo = useCallback(
    (next: number) => {
      if (total === 0) return;
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  if (loading) {
    return (
      <div className="skeleton-shimmer animate-shimmer h-[420px] w-full md:h-[600px]" />
    );
  }

  if (total === 0) {
    return (
      <div className="flex h-[420px] w-full flex-col items-center justify-center bg-primary-900 text-center md:h-[600px]">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-800 text-3xl text-primary-300">
          <i className="ri-film-line" />
        </span>
        <p className="mt-4 font-heading text-lg font-bold text-background-50">
          暂无焦点推荐内容
        </p>
        <p className="mt-2 text-sm text-background-300">精彩内容正在整理中，敬请期待</p>
      </div>
    );
  }

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden md:h-[600px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {works.map((work, i) => (
        <div
          key={work.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={work.backdrop}
            alt={`${work.title} 主视觉`}
            title={`${work.title} 韩国伦理影视作品`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/65 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative mx-auto flex h-full w-full items-center px-4 md:px-8">
        <div className="w-full max-w-2xl">
          <h1 className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold tracking-wide text-background-50">
            <i className="ri-fire-line text-sm" />
            韩国伦理在线观看 · 精品伦理影视推荐
          </h1>

          {works.map((work, i) => (
            <div
              key={work.id}
              className={`mt-5 transition-all duration-500 ${
                i === index ? "block opacity-100" : "hidden opacity-0"
              }`}
            >
              <h2 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                {work.title}
              </h2>
              <p className="mt-2 text-sm font-medium text-background-300">
                {work.originalTitle} · {work.year} · {work.duration}
              </p>
              <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-200 sm:block">
                {work.synopsis}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {work.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-100"
                  >
                    {genre}
                  </span>
                ))}
                <span className="flex items-center gap-1 rounded-full bg-background-50/15 px-3 py-1 text-xs font-semibold text-accent-300">
                  <i className="ri-star-fill text-[11px]" />
                  {work.rating.toFixed(1)} 分
                </span>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${work.id}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看作品详情
                </Link>
                <Link
                  to={`/category/${work.type}`}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-movie-2-line text-lg" />
                  浏览同类作品
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 right-4 flex items-center gap-3 md:right-8">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(index - 1)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/40 text-xl text-background-50 transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(index + 1)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-primary-950/40 text-xl text-background-50 transition-colors hover:bg-primary-950/70"
        >
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      <div className="absolute bottom-6 left-4 flex items-center gap-2 md:left-8">
        {works.map((work, i) => (
          <button
            key={work.id}
            type="button"
            aria-label={`切换到 ${work.title}`}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-accent-500" : "w-2 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}